import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import usePlayerBadges from '../../hooks/usePlayerBadges';
import useResolvePlayerMongoId from '../../hooks/useResolvePlayerModel';
import BadgeStatsBlock from '../../components/badges/BadgeStatsBlock';
import SmartImage from '../../components/common/SmartImage';

/* Clipboard helper */
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

/* Progress ring (simple) */
function ProgressRing({
  size = 140,
  stroke = 10,
  value = 0,
  color = '#6366f1',
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, value));
  const offset = c - (pct / 100) * c;
  return (
    <svg width={size} height={size} role="img" aria-label={`${pct}%`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="rgba(148,163,184,0.25)"
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset .6s ease' }}
      />
      <text
        x="50%"
        y="50%"
        dy="4px"
        textAnchor="middle"
        fontSize={22}
        fontWeight={600}
        fill={color}
      >
        {pct}%
      </text>
    </svg>
  );
}

/** One-time reset to avoid gradient clip leaking into card text */
const ensureParityReset = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const style = document.createElement('style');
    style.setAttribute('data-badge-detail-reset', 'true');
    style.innerHTML = `
      .badgeDetailCard, .badgeDetailCard * {
        color: inherit;
        -webkit-background-clip: initial !important;
        background-clip: initial !important;
      }
      .badgeDetailCard a, .badgeDetailCard button { color: inherit; }
    `;
    document.head.appendChild(style);
  };
})();

export default function BadgeDetailPage() {
  const { player } = useContext(AuthContext);
  const { darkTheme } = useTheme(); // ✅ use app-wide theme
  const location = useLocation();
  const { playerId: handleParam, badgeIdOrSlug } = useParams();

  // Resolve handle -> mongoId the hook returns { mongoId, handle, loading, error }
  const {
    mongoId,
    handle,
    loading: resolving,
    error: resolveError,
  } = useResolvePlayerMongoId(handleParam);

  const token = player?.token || null;

  // BadgesPage / BadgePage / BadgeGallery
  const { badges, meta, status, error, refetch, refreshing } = usePlayerBadges(
    mongoId,
    {
      token,
      includeRarity: true,
      includeLocked: true, // show unearned too
      product: '', // ← IMPORTANT: include all products
      types: '', // (optional)
      categories: '', // (optional)
      limit: 1000,
      debug: true, // get console logs
      // also consider:
      dedupeStrategy: 'badgeId', // see #2 below
    },
  );

  // Color tokens EXACTLY like BadgeGallery/BadgesPage
  const colors = darkTheme
    ? {
        bgSoft: 'rgba(255,255,255,0.06)',
        border: 'rgba(255,255,255,0.09)',
        text: '#f1f5f9',
        textSub: '#cbd5e1',
        textSubFaint: '#94a3b8',
        accent: 'linear-gradient(90deg,#6366f1,#8b5cf6,#ec4899,#f59e0b)',
        chipBg: 'rgba(255,255,255,0.07)',
        shadow: '0 18px 50px -12px rgba(0,0,0,0.7)',
      }
    : {
        bgSoft: '#f1f5f9',
        border: '#e2e8f0',
        text: '#1f2937',
        textSub: '#4b5563',
        textSubFaint: '#64748b',
        accent: 'linear-gradient(90deg,#6366f1,#8b5cf6,#ec4899,#f59e0b)',
        chipBg: '#ffffff',
        shadow: '0 18px 60px -12px rgba(15,40,75,0.25)',
      };

  useEffect(() => {
    ensureParityReset();
  }, []);

  // Deep-link target: /p/:handle/badges/:badgeIdOrSlug OR /p/:handle/badges#id OR ?b=id
  const deepLinkKey = useMemo(() => {
    if (badgeIdOrSlug) return badgeIdOrSlug;
    const hash = location.hash?.slice(1);
    if (hash) return decodeURIComponent(hash);
    const q = new URLSearchParams(location.search);
    const b = q.get('b');
    return b ? decodeURIComponent(b) : null;
  }, [badgeIdOrSlug, location.hash, location.search]);

  const badge = useMemo(() => {
    if (!deepLinkKey || !Array.isArray(badges)) return null;
    const key = String(deepLinkKey).toLowerCase();
    return (
      badges.find(
        (b) =>
          String(b.slug || '').toLowerCase() === key ||
          String(b.id || '').toLowerCase() === key ||
          String(b._id || '').toLowerCase() === key,
      ) || null
    );
  }, [deepLinkKey, badges]);

  if (resolving) {
    return (
      <main style={wrapMain}>
        <h1 style={titleStyle(colors, true)}>Badge</h1>
        <div style={subtleText(colors)}>Resolving player…</div>
      </main>
    );
  }

  if (!mongoId) {
    return (
      <main style={wrapMain}>
        <h1 style={titleStyle(colors, true)}>Badge</h1>
        <div
          style={{
            ...subtleText(colors),
            color: darkTheme ? '#fca5a5' : '#b91c1c',
          }}
        >
          {resolveError ? String(resolveError) : 'Player not found.'}
        </div>
      </main>
    );
  }

  const prettyId =
    handle || handleParam || player?.data?.player?.playerId || '';

  const progressPct = badge
    ? Number(badge.progressPercent ?? badge.progress?.percent ?? 0)
    : 0;
  const earned = !!badge?.earnedAt;
  const earnedDate = earned
    ? new Date(badge.earnedAt).toLocaleDateString()
    : null;

  return (
    <main style={wrapMain} aria-labelledby="badge-detail-title">
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 18,
        }}
      >
        <h1
          id="badge-detail-title"
          style={{
            ...titleStyle(colors, true),
            background: colors.accent,
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            margin: 0,
            fontSize: 30,
          }}
        >
          Badge
        </h1>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link
            to={`/p/${prettyId}/badges`}
            style={pillBtn(colors)}
            aria-label="Back to badges"
          >
            ← Back
          </Link>
          <button
            onClick={() => refetch()}
            disabled={refreshing}
            style={pillBtn(colors)}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      {status === 'loading' && (
        <div style={subtleText(colors)}>Loading badge data…</div>
      )}

      {error && (
        <div
          style={{
            ...subtleText(colors),
            color: darkTheme ? '#fca5a5' : '#b91c1c',
          }}
        >
          {String(error)}
        </div>
      )}

      {!badge && status === 'success' && (
        <div style={subtleText(colors)}>Badge not found.</div>
      )}

      {badge && (
        <div
          className="badgeDetailCard"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 40,
            alignItems: 'flex-start',
            color: colors.text, // ensure readable text
          }}
        >
          {/* Left: hero icon with ring */}
          <div style={{ position: 'relative' }}>
            <div style={heroIconWrap(colors, darkTheme)}>
              <SmartImage
                src={badge.iconUrl}
                alt={badge.name}
                size={180}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
                loading="lazy"
              />
            </div>

            {!earned && progressPct > 0 && (
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%,-50%)',
                  pointerEvents: 'none',
                }}
              >
                <ProgressRing
                  size={170}
                  stroke={10}
                  value={progressPct}
                  color={darkTheme ? '#6366f1' : '#4f46e5'}
                />
              </div>
            )}
          </div>

          {/* Right: details */}
          <div style={{ flex: '1 1 340px', minWidth: 280, maxWidth: 620 }}>
            <h2
              style={{
                margin: '0 0 8px',
                fontSize: 26,
                fontWeight: 700,
                letterSpacing: '.6px',
                color: colors.text,
              }}
            >
              {badge.name}
            </h2>

            <div
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: colors.textSub,
                marginBottom: 14,
                whiteSpace: 'pre-wrap',
              }}
            >
              {badge.desc || badge.description || 'No description.'}
            </div>

            {/* Rich stats (rarity %, global counts, etc.) */}
            <BadgeStatsBlock badge={badge} meta={meta} colors={colors} />

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                marginBottom: 20,
              }}
            >
              {earned && (
                <span style={chip(colors)}>Earned {earnedDate || ''}</span>
              )}
              {!earned && progressPct > 0 && (
                <span style={chip(colors)}>Progress {progressPct}%</span>
              )}
              {!earned && progressPct <= 0 && (
                <span style={chip(colors)}>Locked</span>
              )}
              {badge.tier && (
                <span style={chip(colors)}>Tier {badge.tier}</span>
              )}
              {badge.category && (
                <span style={chip(colors)}>{badge.category}</span>
              )}
              {badge.rarityText && (
                <span style={chip(colors)}>{badge.rarityText}</span>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                style={pillBtn(colors)}
                onClick={async () => {
                  const base =
                    window.location.origin || 'https://www.JoeGalaxy.NET';
                  const link = `${base}/p/${prettyId}/badges#${encodeURIComponent(
                    badge.slug || badge.id || badge._id,
                  )}`;
                  if (await copyText(link)) alert('Link copied');
                }}
              >
                Copy Link
              </button>
              <button
                style={pillBtn(colors)}
                onClick={() => refetch()}
                disabled={refreshing}
              >
                {refreshing ? 'Refreshing…' : 'Reload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

/* Styles */
const wrapMain = {
  padding: '18px clamp(10px,3vw,28px) 30px',
  maxWidth: 1100,
  margin: '0 auto',
};

const titleStyle = (colors, gradient = false) => ({
  fontSize: 28,
  fontWeight: 800,
  letterSpacing: '.5px',
  margin: 0,
  color: gradient ? 'transparent' : colors.text,
});

const subtleText = (colors) => ({
  fontSize: 14,
  color: colors.textSubFaint,
});

const pillBtn = (colors) => ({
  border: `1px solid ${colors.border}`,
  background: colors.chipBg,
  color: colors.text,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 600,
  borderRadius: 999,
  cursor: 'pointer',
  letterSpacing: '.4px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  transition:
    'background .3s, box-shadow .35s, transform .35s, border-color .35s',
});

const chip = (colors) => ({
  background: colors.bgSoft,
  color: colors.textSub,
  padding: '4px 10px 4px',
  fontSize: 11.5,
  fontWeight: 600,
  borderRadius: 999,
  letterSpacing: '.4px',
  lineHeight: 1.2,
  border: `1px solid ${colors.border}`,
});

const heroIconWrap = (colors, dark) => ({
  width: 200,
  height: 200,
  borderRadius: 40,
  background: colors.bgSoft,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: dark
    ? '0 4px 28px -8px rgba(0,0,0,.85), 0 0 0 1px rgba(255,255,255,.08)'
    : '0 10px 30px -10px rgba(15,40,75,.3), 0 0 0 1px #e2e8f0',
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${colors.border}`,
});
